import { useEffect, useLayoutEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { tourBus } from "@/lib/tour";

// Lightweight 5-step tour shown the first time a user reaches /home after
// completing onboarding. Highlights bottom-dock targets with a spotlight
// cutout + tooltip card. Persists completion to profiles.walkthrough_complete.

type Step = {
  selector: string;
  title: string;
  body: string;
  placement?: "above" | "below";
};

const STEPS: Step[] = [
  {
    selector: "[data-tour='dock-scan']",
    title: "scan & verify",
    body: "tap the center button to photograph your garment. fig reads the maker, era, and whether it's the real thing — so you actually know what you own.",
    placement: "above",
  },
  {
    selector: "[data-tour='dock-scan']",
    title: "what it's worth",
    body: "every verified piece comes with a comp range — so you can see if that $40 coat you bought is actually worth $400!",
    placement: "above",
  },
  {
    selector: "[data-tour='dock-scan']",
    title: "file it your way",
    body: "send the piece to your archive, share it to your closet, or keep it private. one scan, however you want to keep it.",
    placement: "above",
  },
  {
    selector: "[data-tour='dock-feed']",
    title: "the feed",
    body: "the front page of vintage. see what other collectors are scanning, hunting, and listing right now. follow the people whose taste matches yours.",
    placement: "above",
  },
  {
    selector: "[data-tour='dock-grails']",
    title: "your grails",
    body: "the pieces you're hunting for. save them here and we'll tap you on the shoulder the moment one surfaces in the wild.",
    placement: "above",
  },
  {
    selector: "[data-tour='dock-you']",
    title: "your closet",
    body: "your personal archive. every piece you've scanned lives here — keep it private, share it to your profile, or link the listing if it's for sale. one closet, every record.",
    placement: "above",
  },
];

interface Rect { top: number; left: number; width: number; height: number; }

export function WalkthroughOverlay() {
  const { user } = useAuth();
  const userId = user?.id;
  const location = useLocation();
  const nav = useNavigate();
  const [active, setActive] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  // Session-local "we already finished" flag. Without this, finishing
  // the tour flipped `active` back to false synchronously, which
  // re-fired the deciding-whether-to-run effect *before* the DB write
  // to walkthrough_complete had committed — so the effect read the
  // stale row and immediately restarted the tour.
  const [completed, setCompleted] = useState(false);
  const forceStart = (location.state as { startWalkthrough?: boolean } | null)?.startWalkthrough === true;

  // Decide whether to run.
  useEffect(() => {
    if (!userId || active || completed) return;
    // The newer Field Kit TourOverlay is the canonical tutorial replay flow.
    // If it's running (or about to), suppress the older walkthrough so the
    // two don't stack on top of each other. Subscribing here also catches
    // the case where the user kicks off the field-kit replay from Settings
    // mid-walkthrough — the old overlay folds away cleanly.
    const fieldKitActive = () => tourBus.current() != null;
    if (fieldKitActive()) {
      setCompleted(true);
      return;
    }
    let cancelled = false;
    let timer = 0;
    const unsub = tourBus.subscribe((idx) => {
      if (idx != null && !cancelled) {
        // Field kit just started — abort and unmount this overlay.
        setActive(false);
        setCompleted(true);
        window.clearTimeout(timer);
      }
    });
    const start = (clearRouteState = false) => {
      setIdx(0);
      timer = window.setTimeout(() => {
        if (cancelled || fieldKitActive()) return;
        setActive(true);
        if (clearRouteState) nav(location.pathname, { replace: true, state: null });
      }, 400);
    };
    (async () => {
      if (forceStart) {
        start(true);
        return;
      }
      const { data } = await supabase
        .from("profiles")
        .select("onboarding_complete, walkthrough_complete")
        .eq("id", userId)
        .maybeSingle();
      if (cancelled) return;
      const row = data as { onboarding_complete?: boolean; walkthrough_complete?: boolean } | null;
      if (row?.onboarding_complete && !row.walkthrough_complete) {
        start();
      }
    })();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      unsub();
    };
  }, [userId, active, completed, forceStart, location.pathname, nav]);

  // Measure the highlighted target.
  useLayoutEffect(() => {
    if (!active) return;
    const update = () => {
      const step = STEPS[idx];
      const el = document.querySelector(step.selector) as HTMLElement | null;
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      const pad = 8;
      setRect({
        top: r.top - pad,
        left: r.left - pad,
        width: r.width + pad * 2,
        height: r.height + pad * 2,
      });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [active, idx]);

  const finish = async () => {
    // Flip the session-local guard BEFORE setActive so the deciding
    // effect can't re-fire while the DB update is still in flight.
    setCompleted(true);
    setActive(false);
    if (userId) {
      await supabase
        .from("profiles")
        .update({ walkthrough_complete: true } as never)
        .eq("id", userId);
    }
  };

  const back = () => setIdx((i) => Math.max(0, i - 1));

  if (!active) return null;
  const step = STEPS[idx];
  const isLast = idx === STEPS.length - 1;

  // Tooltip placement — always centered. The spotlight cutout still pulls
  // attention to the right target; the card itself just sits in the middle
  // of the viewport so it never runs off-screen on narrow devices.
  const vw = typeof window !== "undefined" ? window.innerWidth : 390;
  const SIDE_PAD = 16;
  const TOOLTIP_W = Math.min(320, vw - SIDE_PAD * 2);
  const tooltipStyle: React.CSSProperties = {
    position: "fixed", left: "50%", top: "50%",
    transform: "translate(-50%, -50%)", width: TOOLTIP_W,
    maxHeight: `calc(100dvh - ${SIDE_PAD * 2}px - env(safe-area-inset-top, 0px))`,
    overflowY: "auto",
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 90, pointerEvents: "auto" }} aria-modal="true" role="dialog">
      {/* SVG mask backdrop with cutout */}
      <svg width="100%" height="100%" style={{ position: "absolute", inset: 0 }}>
        <defs>
          <mask id="walkthrough-mask">
            <rect width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left} y={rect.top}
                width={rect.width} height={rect.height}
                rx={12} ry={12}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(20,18,14,0.72)" mask="url(#walkthrough-mask)" />
      </svg>

      {rect && (
        <div
          style={{
            position: "fixed",
            top: rect.top, left: rect.left,
            width: rect.width, height: rect.height,
            border: "2px solid hsl(var(--marigold))",
            borderRadius: 12,
            pointerEvents: "none",
            boxShadow: "0 0 0 4px rgba(255,255,255,0.15)",
          }}
        />
      )}

      <div style={{
        ...tooltipStyle,
        background: "hsl(var(--cream))",
        border: "1.5px solid hsl(var(--ink))",
        padding: 16,
        boxShadow: "0 12px 28px rgba(0,0,0,0.35)",
      }}>
        <div className="caps" style={{ color: "hsl(var(--rust))", fontSize: 10, marginBottom: 8, letterSpacing: "0.18em" }}>
          a field guide · {String(idx + 1).padStart(2, "0")} / {String(STEPS.length).padStart(2, "0")}
        </div>
        <div style={{
          fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 900, fontStyle: "italic", fontSize: 26,
          color: "hsl(var(--ink))", lineHeight: 1.05, marginBottom: 8, letterSpacing: "-0.02em",
        }}>
          {step.title}
        </div>
        <div style={{
          fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 14,
          color: "hsl(var(--ink-2))", lineHeight: 1.5, marginBottom: 16,
        }}>
          {step.body}
        </div>
        {/* Progress dots */}
        <div style={{ display: "flex", gap: 6, marginBottom: 14, justifyContent: "center" }}>
          {STEPS.map((_, i) => (
            <span
              key={i}
              style={{
                width: i === idx ? 18 : 6, height: 6, borderRadius: 3,
                background: i === idx ? "hsl(var(--rust))" : "hsl(var(--sand-deep) / 0.4)",
                transition: "width 200ms ease-out, background 200ms ease-out",
              }}
            />
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <button
            type="button"
            onClick={finish}
            style={{
              background: "none", border: "none", padding: 0, cursor: "pointer",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
              letterSpacing: "0.12em", textTransform: "uppercase",
              color: "hsl(var(--ink-2))",
            }}
          >
            Skip
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            {idx > 0 && (
              <button
                type="button"
                onClick={back}
                style={{
                  background: "hsl(var(--cream))",
                  color: "hsl(var(--ink))",
                  border: "1px solid hsl(var(--ink))",
                  padding: "10px 14px",
                  cursor: "pointer",
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                  letterSpacing: "0.15em", textTransform: "uppercase",
                }}
              >
                ← Back
              </button>
            )}
            <button
              type="button"
              onClick={() => isLast ? finish() : setIdx((i) => i + 1)}
              style={{
                background: "hsl(var(--ink))",
                color: "hsl(var(--ink-fg))",
                border: "1px solid hsl(var(--ink))",
                padding: "10px 14px",
                cursor: "pointer",
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                letterSpacing: "0.15em", textTransform: "uppercase",
              }}
            >
              {isLast ? "begin →" : "Next →"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}